import type { PendingSchemaEdit } from '@/features/schema/fork'
import { apiClient } from './client'
import type { ConceptSeverity, Schema, ValidationFinding } from './schemas'

export type SchemaRunStatus =
  | 'queued'
  | 'running'
  | 'awaiting_input'
  | 'finished_success'
  | 'finished_error'
export type SchemaSessionStatus = 'running' | 'awaiting_review' | 'closed'
export type SchemaCaseType =
  | 'financial'
  | 'narcotics'
  | 'missing_person'
  | 'fraud'
  | 'osint'
  | 'other'
export type SchemaEvidenceType =
  | 'call_records'
  | 'bank_statements'
  | 'transcripts'
  | 'social_media_exports'
  | 'surveillance'
  | 'ids'
export type SchemaQuestionTemplate =
  | 'contact_network'
  | 'location_on_date'
  | 'money_between_accounts'
  | 'units_and_personnel'
/** Catalogue domains a session without files covers beyond pole-basic (PRD-0073); core is always in. */
export type SchemaFocusDomain = 'financial' | 'crypto' | 'comms' | 'geo' | 'corporate' | 'military'
export type SchemaSubjectRole = 'suspect' | 'victim' | 'witness' | 'org'

export interface SchemaSubject {
  role: SchemaSubjectRole
  count: number
}

export interface SchemaTimeWindow {
  start?: string | null
  end?: string | null
}

export interface SchemaSessionIntake {
  document_ids: string[]
  case_type: SchemaCaseType
  investigation_goal: string
  primary_questions: SchemaQuestionTemplate[]
  focus_domains: SchemaFocusDomain[]
  custom_questions: string[]
  subjects: SchemaSubject[]
  evidence_types: SchemaEvidenceType[]
  time_window: SchemaTimeWindow | null
  geographic_scope: string | null
  notes: string
}

export interface CompiledSchemaIntake {
  selected_domains: string[]
  required_patterns: string[][]
  required_properties: {
    owner_kind: string
    owner_label: string
    name: string
    reason: string
  }[]
  temporal_relationships: string[]
  geo_entities: string[]
  trackable_entities: string[]
  required_entities: string[]
  analyzer_evidence_types: Record<string, string[]>
  /** Set only while the form selects no file (PRD-0073): what every agent proposal keeps. */
  minimum_schema?: SchemaMinimum | null
}

export interface SchemaMinimum {
  schema_id: string
  name: string
  version: number
  entity_types: string[]
  relationship_types: string[]
  patterns: string[][]
}

export interface SchemaNovelLabel {
  kind: 'novel_entity' | 'novel_relationship'
  label: string
  nearest: string | null
  severity: ConceptSeverity
  message: string
  justification: string
}

export interface SchemaEvidencePointer {
  kind: 'form' | 'question' | 'document' | 'schema'
  ref: string
}

export interface SchemaTypeReason {
  why: string
  basis?: 'documents' | 'intake' | 'existing_schema'
  evidence: SchemaEvidencePointer[]
}

export interface SchemaQuestionCoverage {
  question: string
  answerable: boolean
  patterns: string[][]
  properties: string[]
  views: ('map' | 'timeline' | 'movement_track')[]
  explanation: string | null
}

export interface SchemaProposalChange {
  op: 'add' | 'remove' | 'change'
  target: string
  reason: string
}

export interface SchemaReferenceFile {
  document_id: string
  filename: string
  missing?: boolean
}

export interface SchemaDocumentReview {
  document_id: string
  relevance: 'primary' | 'supporting' | 'marginal' | 'uncertain' | 'unavailable'
  reason: string
}

export interface SchemaProposal {
  name: string
  description: string | null
  schema: Record<string, unknown>
  rationale: string | null
  typeReasons: Record<string, SchemaTypeReason>
  sources?: SchemaReferenceFile[]
  documentReviews?: SchemaDocumentReview[]
  coverage: SchemaQuestionCoverage[]
  changes: SchemaProposalChange[]
  novel: SchemaNovelLabel[]
  warnings: ValidationFinding[]
  errors: ValidationFinding[]
}

export interface SchemaClarificationQuestion {
  question: string
  options: string[]
}

export interface SchemaClarification {
  context: string | null
  questions: SchemaClarificationQuestion[]
  answers: { question: string; answer: string }[] | null
  rounds: number
}

export interface SchemaSessionRun {
  id: string
  sessionId: string
  agentThreadId: string | null
  status: SchemaRunStatus
  stepsCompleted: number
  stepsTotal: number | null
  errorMessage: string | null
  startedAt: string
  finishedAt: string | null
  proposal: SchemaProposal | null
  clarification: SchemaClarification | null
  progress: Record<string, SchemaSessionEvent>
}

export interface SchemaSession {
  id: string
  caseId: string
  status: SchemaSessionStatus
  intake: SchemaSessionIntake
  initializationSources?: SchemaReferenceFile[]
  reviewProposal?: SchemaProposal | null
  compiledIntake: CompiledSchemaIntake
  /** Slice-01 compatibility conveniences. */
  documentIds: string[]
  notes: string
  authorId: string | null
  authorName: string
  createdAt: string
  updatedAt: string
  latestRun: SchemaSessionRun | null
  draft: Schema | null
  acceptedSchemaId: string | null
  workingSchema: Schema | null
  pendingProposal: SchemaProposal | null
  pendingBase: Schema | null
  baseSchemaId: string | null
  agentThreadId: string | null
}

export interface SchemaSessionStartBody {
  documentIds: string[]
  caseType: SchemaCaseType
  investigationGoal: string
  primaryQuestions: SchemaQuestionTemplate[]
  focusDomains: SchemaFocusDomain[]
  customQuestions: string[]
  subjects: SchemaSubject[]
  evidenceTypes: SchemaEvidenceType[]
  timeWindow: SchemaTimeWindow | null
  geographicScope: string | null
  notes: string
}

export interface SchemaSessionAcceptBody {
  name?: string
  confirmedHeadId?: string
}

export interface SchemaClarificationAnswerBody {
  answers: { question: string; answer: string }[]
}

export const schemaSessionsApi = {
  current: (caseId: string) =>
    apiClient.get<SchemaSession>(`/v1/cases/${caseId}/schema-sessions/current`),

  start: (caseId: string, body: SchemaSessionStartBody) =>
    apiClient.post<SchemaSession>(`/v1/cases/${caseId}/schema-sessions`, body),

  answer: (caseId: string, sessionId: string, body: SchemaClarificationAnswerBody) =>
    apiClient.post<SchemaSession>(`/v1/cases/${caseId}/schema-sessions/${sessionId}/answer`, body),

  accept: (caseId: string, sessionId: string, body: SchemaSessionAcceptBody = {}) =>
    apiClient.post<SchemaSession>(`/v1/cases/${caseId}/schema-sessions/${sessionId}/accept`, body),

  discard: (caseId: string, sessionId: string) =>
    apiClient.delete<void>(`/v1/cases/${caseId}/schema-sessions/${sessionId}`),

  repair: (caseId: string, sessionId: string, feedback = '') =>
    apiClient.post<SchemaSession>(`/v1/cases/${caseId}/schema-sessions/${sessionId}/repair`, {
      feedback,
    }),

  editDraft: (caseId: string, sessionId: string, edit: PendingSchemaEdit) =>
    apiClient.patch<SchemaSession>(`/v1/cases/${caseId}/schema-sessions/${sessionId}/draft`, {
      edit,
    }),

  updateIntake: (caseId: string, sessionId: string, body: SchemaSessionStartBody) =>
    apiClient.patch<SchemaSession>(`/v1/cases/${caseId}/schema-sessions/${sessionId}/intake`, body),

  regenerate: (caseId: string, sessionId: string) =>
    apiClient.post<SchemaSession>(
      `/v1/cases/${caseId}/schema-sessions/${sessionId}/regenerate`,
      {}
    ),

  resolveProposal: (caseId: string, sessionId: string, apply: boolean) =>
    apiClient.post<SchemaSession>(
      `/v1/cases/${caseId}/schema-sessions/${sessionId}/proposals/${apply ? 'apply' : 'discard'}`,
      {}
    ),

  eventsUrl: (caseId: string, sessionId: string) =>
    `/api/v1/cases/${caseId}/schema-sessions/${sessionId}/events`,
}

export type SchemaSessionEvent =
  | {
      type: 'run_status'
      status?: string
      steps_completed?: number
      steps_total?: number | null
    }
  | {
      type: 'digest_ready'
      files?: { document_id: string; filename: string; missing: boolean }[]
    }
  | { type: 'step_started'; tool_name?: string; steps_completed?: number }
  /** The run appended messages to the session's chat transcript. */
  | { type: 'transcript_updated'; messages?: number }
  | { type: 'clarification_requested' }
  | { type: 'run_finished'; status?: string; error?: string | null }
